import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  MapPin,
  WifiHigh,
  Thermometer,
  Path,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DriverBehavior({ datasetId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${API}/analytics/driver-behavior?dataset_id=${datasetId}`
        );
        setData(res.data);
      } catch (e) {
        console.error("Failed to load driver behavior", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [datasetId]);

  if (loading || !data)
    return (
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm p-4">
        <div className="animate-pulse text-xs text-zinc-500 font-mono">
          Loading driver data...
        </div>
      </Card>
    );

  const connectivityScore = data.connectivity.ratio;
  const stabilityScore =
    data.temperature_stability.total_readings > 0
      ? Math.round(
          (data.temperature_stability.stable_readings /
            data.temperature_stability.total_readings) *
            100
        )
      : 100;

  const metrics = [
    {
      icon: data.is_stationary ? MapPin : Path,
      label: "Route Status",
      value: data.is_stationary
        ? "Stationary"
        : `${data.total_distance_km} km`,
      sub: `${data.unique_locations} unique position${data.unique_locations !== 1 ? "s" : ""}`,
      color: "text-teal-400",
    },
    {
      icon: WifiHigh,
      label: "Connectivity",
      value: `${connectivityScore}%`,
      sub: `${data.connectivity.realtime} realtime / ${data.connectivity.buffered} buffered`,
      color:
        connectivityScore > 90 ? "text-emerald-400" : "text-amber-400",
    },
    {
      icon: Thermometer,
      label: "Temp Stability",
      value: `${stabilityScore}%`,
      sub: `Avg \u0394${data.temperature_stability.avg_change}\u00b0F \u2022 Max \u0394${data.temperature_stability.max_change}\u00b0F`,
      color: stabilityScore > 80 ? "text-emerald-400" : "text-amber-400",
    },
  ];

  return (
    <Card
      data-testid="driver-behavior"
      className="bg-zinc-900 border-zinc-800 rounded-sm p-4"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
          Driver & Transport
        </h3>
        <Badge variant="outline" className="border-zinc-700 text-xs font-mono">
          {data.total_events} events
        </Badge>
      </div>

      <div className="space-y-2.5">
        {metrics.map((m) => (
          <div
            key={m.label}
            className="flex items-center gap-3 p-2.5 rounded-sm bg-zinc-950/50 hover:bg-zinc-950 transition-colors duration-200"
          >
            <m.icon size={20} weight="duotone" className={m.color} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">
                {m.label}
              </p>
              <p className="text-sm font-mono font-semibold text-zinc-200">
                {m.value}
              </p>
              {m.sub && (
                <p className="text-[10px] text-zinc-500 font-mono">
                  {m.sub}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
